import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const connectionTypeTarget = {
  kind: "configuration",
  field: "connectionType",
} as const;

function apiBaseUrl(
  configuration: Partial<
    Record<"connectionType" | "consoleId" | "consoleUrl", string>
  >
) {
  if (configuration.connectionType === "local") {
    const baseUrl = (configuration.consoleUrl ?? "").trim().replace(/\/+$/, "");
    return baseUrl ? `${baseUrl}/proxy/protect/integration` : "";
  }

  const consoleId = (configuration.consoleId ?? "").trim();
  return consoleId
    ? `https://api.ui.com/v1/connector/consoles/${consoleId}/proxy/protect/integration`
    : "";
}

export const unifiProtect = defineConnector({
  identifier: "unifiProtect",
  name: "UniFi Protect",
  tagLine: "Video security management for UniFi deployments",
  keywords: "ubiquiti cameras nvr surveillance",
  logo,
  protocol: "http",
  tags: ["security", "it"],
  defaultUrlPattern: (configuration) =>
    exactUrlToRegex(apiBaseUrl(configuration)),
  auth: headerAuth([
    { name: "X-API-Key", value: { from: "secret", field: "apiKey" } },
  ]),
  steps: [
    {
      title: "Choose a connection type",
      items: [
        {
          kind: "select",
          label: "Connection type",
          required: true,
          defaultValue: "cloud",
          target: connectionTypeTarget,
          options: [
            { value: "cloud", label: "Cloud" },
            { value: "local", label: "Local console" },
          ],
        },
      ],
    },
    {
      title: "Create a UniFi API key",
      items: [
        {
          kind: "markdown",
          content: ({ connectionType }) =>
            connectionType === "local"
              ? "Create an API key under **Integrations** on the local console."
              : "Create an API key under **Settings → API Keys** in [UniFi Site Manager](https://unifi.ui.com).",
        },
      ],
    },
    {
      title: "Enter your UniFi Protect details",
      items: [
        {
          kind: "input",
          label: "Console ID",
          required: true,
          target: { kind: "configuration", field: "consoleId" },
          helperText:
            "Use the host `id` returned by [List Hosts](https://developer.ui.com/site-manager/v1.0.0/listhosts).",
          visibleWhen: [{ target: connectionTypeTarget, value: "cloud" }],
        },
        {
          kind: "baseUrl",
          label: "Console URL",
          required: true,
          placeholder: "https://unifi.example.com",
          target: { kind: "configuration", field: "consoleUrl" },
          visibleWhen: [{ target: connectionTypeTarget, value: "local" }],
        },
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: (configuration) => ({
      url: `${apiBaseUrl(configuration)}/v1/meta/info`,
      method: "GET",
      headers: { Accept: "application/json" },
    }),
  },
});
